import { PropsWithChildren, ReactNode, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';

import { CloseIcon, FullScreenIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { FlexGap } from './FlexGap';
import { Heading3 } from './Text';
import { Button } from './button/Button';
import { IconButton } from './button/IconButton';

const ContentsContainer = styled(Box)`
	background: ${tokens.surface.secondary};
	overflow-y: auto;
	min-height: 0;
`;

function Contents(props: PropsWithChildren<BoxProps>) {
	const { p = tokens.padding.container, pt, pb, pl, pr, px, py, ...rest } = props;

	return (
		<ContentsContainer
			p={p}
			px={px}
			py={py}
			pt={pt}
			pb={pb}
			pl={pl}
			pr={pr}
			flexGrow={1}
			flexShrink={1}
			{...rest}
		/>
	);
}

const HeaderContainer = styled(Box)<{ $tabs: boolean }>`
	background: ${tokens.surface.white};
	${(props) => !props.$tabs && `border-bottom: 1px solid ${tokens.border.default};`}
`;

const HeaderRow = styled(FlexGap)<{ $tabs: boolean }>`
	align-items: center;
	padding: ${tokens.padding.container}px;
	${(props) => props.$tabs && `padding-bottom: 0;`}
`;

const FooterContainer = styled(Flex)`
	background: ${tokens.surface.white};
	border-top: 1px solid ${tokens.border.default};
	justify-content: flex-end;
	align-items: center;
	gap: ${tokens.gap.element}px;
	padding: ${tokens.padding.control}px ${tokens.padding.container}px;
	box-sizing: border-box;
`;

function Escape(props: { onEscape: () => void }) {
	useLayoutEffect(() => {
		function onKeyDown(event: KeyboardEvent) {
			const element = document.activeElement as HTMLElement;

			if (event.key === 'Escape' && element?.tabIndex === -1) {
				props.onEscape();
			}
		}

		document.addEventListener('keydown', onKeyDown);

		return () => document.removeEventListener('keydown', onKeyDown);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}

function Header(
	props: PropsWithChildren<
		BoxProps & {
			title?: ReactNode;
			icon?: ReactNode;
			tabs?: boolean;
			onFullScreen?: () => void;
			onClose?: () => void;
			closeLink?: string;
			leftAccessory?: ReactNode;
			rightAccessory?: ReactNode;
		}
	>,
) {
	const history = useHistory();

	return (
		<HeaderContainer flexGrow={0} flexShrink={0} $tabs={!!props.tabs}>
			<HeaderRow gap={tokens.gap.list} $tabs={!!props.tabs}>
				{props.leftAccessory}
				<FlexGap
					alignItems="center"
					gap={tokens.gap.element}
					flexGrow={1}
					flexShrink={1}
					minWidth={0}
				>
					{props.icon && (
						<Box flexShrink={0} color={tokens.icon.primary}>
							{props.icon}
						</Box>
					)}
					<Heading3 flexGrow={1} flexShrink={1} minWidth={0}>
						{props.title ?? props.children}
					</Heading3>
				</FlexGap>
				{props.onFullScreen && (
					<IconButton icon={<FullScreenIcon />} type="secondary" onClick={props.onFullScreen} />
				)}
				{props.rightAccessory}
				{props.onClose && (
					<>
						<Escape onEscape={props.onClose} />
						<IconButton icon={<CloseIcon />} type="secondary" onClick={props.onClose} />
					</>
				)}
				{props.closeLink && (
					<>
						<Escape onEscape={() => history.push(props.closeLink)} />
						<IconButton
							icon={<CloseIcon />}
							type="secondary"
							onClick={() => history.push(props.closeLink)}
						/>
					</>
				)}
			</HeaderRow>
		</HeaderContainer>
	);
}

function Footer(props: PropsWithChildren<BoxProps>) {
	return <FooterContainer width="100%" flexGrow={0} flexShrink={0} {...props} />;
}

function FormFooter(props: {
	small?: boolean;
	onCancel?: () => void;
	onSave?: () => void;
	disabled?: boolean;
	saveTitle?: string;
	cancelTitle?: string;
	cancelLink?: string;
}) {
	const { t } = useTranslation();

	return (
		<Footer>
			{props.onCancel && (
				<Button
					type="secondary"
					title={props.cancelTitle ?? t('modal.footer.form.cancel')}
					small={props.small}
					flexGrow={0}
					flexShrink={0}
					onClick={props.onCancel}
				/>
			)}
			{props.cancelLink && (
				<Button
					type="secondary"
					title={props.cancelTitle ?? t('modal.footer.form.cancel')}
					small={props.small}
					flexGrow={0}
					flexShrink={0}
					link={props.cancelLink}
				/>
			)}
			<Button
				type="primary"
				title={props.saveTitle ?? t('modal.footer.form.save')}
				small={props.small}
				flexGrow={0}
				flexShrink={0}
				onClick={props.onSave}
				disabled={props.disabled}
			/>
		</Footer>
	);
}

function FormFooterFormik(props: {
	small?: boolean;
	onCancel?: () => void;
	disabled?: boolean;
	saveTitle?: string;
	children?: React.ReactNode;
}) {
	const { t } = useTranslation();

	return (
		<Footer>
			<Flex flexGrow={1} justifyContent={'flex-start'}>
				{props.children}
			</Flex>
			<Button
				type="secondary"
				title={t('modal.footer.form.cancel')}
				small={props.small}
				flexGrow={0}
				flexShrink={0}
				onClick={props.onCancel}
			/>
			<Button.Formik
				type="primary"
				title={props.saveTitle ?? t('modal.footer.form.save')}
				small={props.small}
				flexGrow={0}
				flexShrink={0}
				disabled={props.disabled}
			/>
		</Footer>
	);
}

FormFooter.Formik = FormFooterFormik;

const Frame = styled(Flex)`
	min-height: 0;
	/* Nested calc() inside min() is ignored, so keep a plain calc() fallback. */
	max-height: calc(100dvh - 40px);
	max-height: min(100dvh - 40px, 720px);
`;

export function Modal(
	props: PropsWithChildren<BoxProps & { width?: string | number; height?: string | number }>,
) {
	return (
		<Frame width={props.width ?? 600} height={props.height} flexDirection="column">
			{props.children}
		</Frame>
	);
}

Modal.Header = Header;
Modal.Contents = Contents;
Modal.Footer = Footer;
Modal.FormFooter = FormFooter;
